/**
 * The case: an application, and the policy it becomes.
 *
 * WHY ONE RECORD AND NOT TWO
 * An application and a policy are the same piece of business at two moments in
 * its life. Modelling them separately creates a handoff, and the handoff is
 * where the interesting data goes missing — the date it was submitted, who
 * wrote it, what the carrier asked for and how long it took to arrive. Agencies
 * do not say "the application became a policy"; they say "the case issued". One
 * record, one status, one history.
 *
 * WHY THIS MATTERS MORE THAN ANYTHING ELSE IN THE SYSTEM
 * Before this existed, Cellvora could report on phone activity and nothing
 * else. Calls placed, leads worked, dispositions recorded — all of it activity,
 * none of it business. An agency cannot run on that. The questions an owner
 * actually asks are what did we write, what is stuck, and who is placing it,
 * and every one of them needs the case.
 */

/* -------------------------------------------------------------- the case -- */

export const POLICY_STATUSES = [
  'quoted',
  'submitted',
  'underwriting',
  'approved',
  'issued',
  'active',
  'declined',
  'postponed',
  'withdrawn',
  'not_taken',
  'lapsed',
  'surrendered',
  'death_claim',
  'cancelled',
] as const;
export type PolicyStatus = (typeof POLICY_STATUSES)[number];

/**
 * What each status means, in the words the floor uses.
 *
 * `not_taken` earns its place. It means the carrier approved the case, issued
 * the policy, and the client never paid the first premium — so it never counted
 * and the commission is charged back. Agencies that fold it into "declined"
 * cannot tell the difference between business the carrier refused and business
 * the agency failed to deliver, and only one of those is fixable.
 */
export const POLICY_STATUS_LABEL: Record<PolicyStatus, string> = {
  quoted: 'Quoted',
  submitted: 'Submitted to carrier',
  underwriting: 'In underwriting',
  approved: 'Approved, not yet issued',
  issued: 'Issued, awaiting first premium',
  active: 'In force',
  declined: 'Declined by carrier',
  postponed: 'Postponed by carrier',
  withdrawn: 'Withdrawn by client',
  not_taken: 'Not taken — never paid',
  lapsed: 'Lapsed',
  surrendered: 'Surrendered',
  death_claim: 'Death claim',
  cancelled: 'Cancelled',
};

export type PolicyStage = 'pending' | 'placed' | 'lost' | 'terminated';

/**
 * Which bucket a status counts in.
 *
 * `pending` is the war room: submitted business whose fate is undecided, and
 * the only bucket anybody can still influence. `placed` is business that
 * actually counted. `lost` never placed. `terminated` placed and then ended,
 * which is a different problem with different causes — a lapse is a service
 * failure, a decline is an underwriting one, and reporting them together hides
 * both.
 */
export const POLICY_STAGE: Record<PolicyStatus, PolicyStage> = {
  quoted: 'pending',
  submitted: 'pending',
  underwriting: 'pending',
  approved: 'pending',
  issued: 'pending',
  active: 'placed',
  declined: 'lost',
  postponed: 'pending',
  withdrawn: 'lost',
  not_taken: 'lost',
  lapsed: 'terminated',
  surrendered: 'terminated',
  death_claim: 'terminated',
  cancelled: 'terminated',
};

/** A case nobody can influence any more. */
export function isSettled(status: PolicyStatus): boolean {
  const stage = POLICY_STAGE[status];
  return stage === 'lost' || stage === 'terminated' || status === 'active';
}

/**
 * Which statuses a case may move to next.
 *
 * Deliberately permissive forwards and strict backwards: carriers do move a
 * case back into underwriting after a requirement lands, and a lapsed policy
 * can be reinstated. What a case may never do is return to `quoted`, because
 * that would silently reset the clock every report measures from.
 */
export const POLICY_TRANSITIONS: Record<PolicyStatus, readonly PolicyStatus[]> = {
  quoted: ['submitted', 'withdrawn'],
  submitted: ['underwriting', 'approved', 'declined', 'postponed', 'withdrawn'],
  underwriting: ['approved', 'declined', 'postponed', 'withdrawn'],
  approved: ['issued', 'declined', 'withdrawn', 'not_taken'],
  issued: ['active', 'not_taken', 'withdrawn'],
  active: ['lapsed', 'surrendered', 'death_claim', 'cancelled'],
  postponed: ['submitted', 'underwriting', 'withdrawn', 'declined'],
  declined: ['submitted'],
  withdrawn: ['submitted'],
  not_taken: ['active'],
  lapsed: ['active', 'surrendered', 'cancelled'],
  surrendered: [],
  death_claim: [],
  cancelled: [],
};

export function canTransitionPolicy(from: PolicyStatus, to: PolicyStatus): boolean {
  return POLICY_TRANSITIONS[from].includes(to);
}

/* ------------------------------------------------------------- products -- */

export const PRODUCT_TYPES = [
  'term', 'whole_life', 'universal_life', 'indexed_universal_life',
  'guaranteed_universal_life', 'variable_universal_life',
  'final_expense', 'accidental_death', 'annuity', 'mortgage_protection',
  'critical_illness', 'disability', 'long_term_care', 'other',
] as const;
export type ProductType = (typeof PRODUCT_TYPES)[number];

export const PRODUCT_TYPE_LABEL: Record<ProductType, string> = {
  term: 'Term life',
  whole_life: 'Whole life',
  universal_life: 'Universal life',
  indexed_universal_life: 'Indexed universal life (IUL)',
  guaranteed_universal_life: 'Guaranteed universal life (GUL)',
  variable_universal_life: 'Variable universal life (VUL)',
  final_expense: 'Final expense',
  accidental_death: 'Accidental death',
  annuity: 'Annuity',
  mortgage_protection: 'Mortgage protection',
  critical_illness: 'Critical illness',
  disability: 'Disability',
  long_term_care: 'Long-term care',
  other: 'Other',
};

/* --------------------------------------------------------------- premium -- */

export const PREMIUM_MODES = ['monthly', 'quarterly', 'semiannual', 'annual', 'single'] as const;
export type PremiumMode = (typeof PREMIUM_MODES)[number];

/** How many times a year each mode is billed. `single` is once, ever. */
export const MODE_PER_YEAR: Record<PremiumMode, number> = {
  monthly: 12, quarterly: 4, semiannual: 2, annual: 1, single: 1,
};

export const PREMIUM_MODE_LABEL: Record<PremiumMode, string> = {
  monthly: 'Monthly', quarterly: 'Quarterly', semiannual: 'Twice a year',
  annual: 'Yearly', single: 'Single premium',
};

/**
 * Annualised premium, in cents.
 *
 * Every production figure in the system runs through this, because a $100
 * monthly policy and a $100 annual policy are not the same sale and an agency
 * that reports them together cannot rank its own producers. A single-premium
 * contract annualises to itself: it is one payment, not a rate.
 */
export function annualisePremium(modalCents: number, mode: PremiumMode): number {
  if (!Number.isFinite(modalCents) || modalCents < 0) return 0;
  return Math.round(modalCents * MODE_PER_YEAR[mode]);
}

/* -------------------------------------------------- underwriting classes -- */

export const UNDERWRITING_CLASSES = [
  'preferred_plus', 'preferred', 'standard_plus', 'standard',
  'substandard', 'guaranteed_issue', 'simplified_issue', 'unknown',
] as const;
export type UnderwritingClass = (typeof UNDERWRITING_CLASSES)[number];

export const UNDERWRITING_CLASS_LABEL: Record<UnderwritingClass, string> = {
  preferred_plus: 'Preferred plus',
  preferred: 'Preferred',
  standard_plus: 'Standard plus',
  standard: 'Standard',
  substandard: 'Substandard / rated',
  guaranteed_issue: 'Guaranteed issue',
  simplified_issue: 'Simplified issue',
  unknown: 'Not yet classified',
};

/* ---------------------------------------------------------- requirements -- */

/**
 * What a pending case is waiting on.
 *
 * This is the whole pending-case problem in one list. A case does not sit in
 * underwriting because underwriting is slow; it sits there because something
 * specific has not arrived, and somebody could go and get it. A system that
 * says "in underwriting" and stops is describing the delay rather than doing
 * anything about it.
 */
export const REQUIREMENT_KINDS = [
  'paramed_exam', 'attending_physician_statement', 'lab_work', 'phone_interview',
  'medical_records', 'financial_documentation', 'signature', 'amendment',
  'replacement_form', 'illustration', 'beneficiary_details', 'payment_details',
  'motor_vehicle_report', 'other',
] as const;
export type RequirementKind = (typeof REQUIREMENT_KINDS)[number];

export const REQUIREMENT_LABEL: Record<RequirementKind, string> = {
  paramed_exam: 'Paramed exam',
  attending_physician_statement: 'Attending physician statement (APS)',
  lab_work: 'Lab work',
  phone_interview: 'Phone interview',
  medical_records: 'Medical records',
  financial_documentation: 'Financial documentation',
  signature: 'Signature',
  amendment: 'Amendment to sign',
  replacement_form: 'Replacement form',
  illustration: 'Signed illustration',
  beneficiary_details: 'Beneficiary details',
  payment_details: 'Payment details',
  motor_vehicle_report: 'Motor vehicle report',
  other: 'Other',
};

/** Who has to act. Chasing the wrong party is how a case sits for six weeks. */
export const REQUIREMENT_OWNERS = ['client', 'carrier', 'agent', 'third_party'] as const;
export type RequirementOwner = (typeof REQUIREMENT_OWNERS)[number];

export const REQUIREMENT_OWNER_LABEL: Record<RequirementOwner, string> = {
  client: 'The client',
  carrier: 'The carrier',
  agent: 'The agent',
  third_party: 'A third party (doctor, lab, employer)',
};

/* ------------------------------------------------------------- reporting -- */

export interface ProductionTotals {
  /** Cases counted. */
  count: number;
  /** Annualised premium, in cents. */
  annualPremiumCents: number;
  /** Total face amount, in cents. */
  faceCents: number;
}

export const EMPTY_TOTALS: ProductionTotals = { count: 0, annualPremiumCents: 0, faceCents: 0 };

/**
 * Placement rate: of the business whose fate is decided, what share placed.
 *
 * Pending cases are excluded from both halves on purpose. Including them drags
 * the rate down every time an agency has a good submission week, which makes
 * the number move in the opposite direction to the thing it is measuring and
 * teaches everyone to ignore it.
 *
 * Returns null rather than zero when nothing has settled yet — an agency with
 * no decided business does not have a placement rate of 0%, it has no
 * placement rate, and showing 0% reads as catastrophe.
 */
export function placementRate(counts: Record<PolicyStage, number>): number | null {
  const decided = counts.placed + counts.lost + counts.terminated;
  if (decided === 0) return null;
  // Terminated business placed before it ended, so it counts as placed here.
  return (counts.placed + counts.terminated) / decided;
}

/** Groups statuses into stages and counts them. */
export function stageCounts(statuses: readonly PolicyStatus[]): Record<PolicyStage, number> {
  const out: Record<PolicyStage, number> = { pending: 0, placed: 0, lost: 0, terminated: 0 };
  for (const s of statuses) out[POLICY_STAGE[s]] += 1;
  return out;
}
